import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Loader2, Package, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, Plus, X, AlertTriangle, Boxes, History } from "lucide-react";
import { toast } from "sonner";

const TIPO_ICON = {
  carico: { icon: ArrowUpCircle, color: "#1E4620", bg: "#E8F3EB", label: "Carico" },
  scarico: { icon: ArrowDownCircle, color: "#B30000", bg: "#FFEBEB", label: "Scarico" },
  trasferimento: { icon: ArrowLeftRight, color: "#8A4D0F", bg: "#FFF4E5", label: "Trasferimento" },
};

export default function Magazzino() {
  const [tab, setTab] = useState("giacenze");
  const [loading, setLoading] = useState(true);
  const [movimenti, setMovimenti] = useState([]);
  const [sotto, setSotto] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [filters, setFilters] = useState({ prodotto_id: "", tipo: "", magazzino: "" });
  const [searchProd, setSearchProd] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    prodotto_id: "",
    tipo: "carico",
    magazzino: "virtuale",
    quantita: 1,
    causale: "",
    note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.prodotto_id) params.set("prodotto_id", filters.prodotto_id);
      if (filters.tipo) params.set("tipo", filters.tipo);
      if (filters.magazzino) params.set("magazzino", filters.magazzino);
      const [m, s, p, c] = await Promise.all([
        api.get(`/magazzino/movimenti?${params}`),
        api.get("/magazzino/sotto-scorta"),
        api.get("/prodotti"),
        api.get("/constants"),
      ]);
      setMovimenti(m.data);
      setSotto(s.data);
      setProdotti(p.data);
      setCategorie(c.data.materiali_categorie || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/magazzino/movimenti", {
        ...form,
        quantita: Number(form.quantita),
      });
      toast.success("Movimento registrato");
      setShowForm(false);
      setForm({ ...form, quantita: 1, causale: "", note: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="magazzino-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Magazzino</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Movimenti, storico e stato scorte
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-movimento-btn">
          <Plus className="w-4 h-4" /> Nuovo movimento
        </button>
      </div>

      {/* Sotto scorta */}
      {sotto.length > 0 && (
        <div className="card-shell p-5 mb-6 border-l-4" style={{ borderLeftColor: "#E07A5F" }} data-testid="sotto-scorta-block">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5" style={{ color: "#E07A5F" }} />
            <h2 className="font-bold" style={{ fontFamily: "Manrope" }}>Prodotti sotto scorta ({sotto.length})</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sotto.map((p) => (
              <div key={p.id} className="text-sm p-2 rounded-md flex justify-between items-center" style={{ background: "var(--surface-muted)" }}>
                <div>
                  <div className="font-semibold">{p.nome}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>{p.sku}</div>
                </div>
                <div className="text-right text-xs">
                  <div>V: <strong>{p.quantita_virtuale}</strong></div>
                  <div>F: <strong>{p.quantita_fisica}</strong></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-lg w-fit" style={{ background: "var(--sidebar)" }}>
        <button
          onClick={() => setTab("giacenze")}
          className="px-4 py-2 text-sm font-medium rounded-md transition-all inline-flex items-center gap-2"
          style={tab === "giacenze" ? { background: "#fff", color: "var(--text)", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" } : { color: "var(--text-muted)" }}
          data-testid="tab-giacenze"
        >
          <Boxes className="w-4 h-4" /> Giacenze
        </button>
        <button
          onClick={() => setTab("movimenti")}
          className="px-4 py-2 text-sm font-medium rounded-md transition-all inline-flex items-center gap-2"
          style={tab === "movimenti" ? { background: "#fff", color: "var(--text)", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" } : { color: "var(--text-muted)" }}
          data-testid="tab-movimenti"
        >
          <History className="w-4 h-4" /> Movimenti
        </button>
      </div>

      {tab === "giacenze" && (
        <GiacenzeView prodotti={prodotti} categorie={categorie} loading={loading} searchProd={searchProd} setSearchProd={setSearchProd} />
      )}

      {tab === "movimenti" && (
        <>
          {/* Filtri */}
          <div className="card-shell p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <select value={filters.prodotto_id} onChange={(e) => setFilters({ ...filters, prodotto_id: e.target.value })}
              className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-prodotto">
              <option value="">Tutti i prodotti</option>
              {prodotti.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
            <select value={filters.tipo} onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
              className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-tipo">
              <option value="">Tutti i tipi</option>
              <option value="carico">Carico</option>
              <option value="scarico">Scarico</option>
              <option value="trasferimento">Trasferimento</option>
            </select>
            <select value={filters.magazzino} onChange={(e) => setFilters({ ...filters, magazzino: e.target.value })}
              className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="filter-magazzino">
              <option value="">Tutti i magazzini</option>
              <option value="virtuale">Virtuale</option>
              <option value="fisico">Fisico</option>
            </select>
            <button onClick={load} className="btn-secondary" data-testid="apply-filters">Applica</button>
          </div>

          <MovimentiView loading={loading} movimenti={movimenti} />
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)}>
          <div className="card-shell w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Nuovo movimento</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Prodotto *</label>
                <select required value={form.prodotto_id} onChange={(e) => setForm({ ...form, prodotto_id: e.target.value })}
                  data-testid="m-prodotto"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }}>
                  <option value="">Seleziona…</option>
                  {prodotti.map((p) => <option key={p.id} value={p.id}>{p.nome} {p.sku ? `(${p.sku})` : ""} - V:{p.quantita_virtuale} F:{p.quantita_fisica}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tipo</label>
                  <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    data-testid="m-tipo"
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                    style={{ borderColor: "var(--border)", background: "#fff" }}>
                    <option value="carico">Carico</option>
                    <option value="scarico">Scarico</option>
                    <option value="trasferimento">Trasferimento</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    {form.tipo === "trasferimento" ? "Da magazzino" : "Magazzino"}
                  </label>
                  <select value={form.magazzino} onChange={(e) => setForm({ ...form, magazzino: e.target.value })}
                    data-testid="m-mag"
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                    style={{ borderColor: "var(--border)", background: "#fff" }}>
                    <option value="virtuale">Virtuale</option>
                    <option value="fisico">Fisico</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Quantità *</label>
                <input type="number" min="0.01" step="0.01" required value={form.quantita}
                  onChange={(e) => setForm({ ...form, quantita: e.target.value })}
                  data-testid="m-qta"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Causale</label>
                <input value={form.causale} onChange={(e) => setForm({ ...form, causale: e.target.value })}
                  placeholder="Es: Arrivo fornitore, Resa installazione…"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="m-submit">Registra</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MovimentiView({ loading, movimenti }) {
  return (
    <div className="card-shell overflow-hidden">
      {loading ? (
        <div className="p-8 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : movimenti.length === 0 ? (
        <div className="p-12 text-center">
          <Package className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Nessun movimento registrato</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--surface-muted)" }}>
              <tr>
                {["Data", "Tipo", "Prodotto", "Magazzino", "Q.tà", "Causale", "Utente"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movimenti.map((m) => {
                const ti = TIPO_ICON[m.tipo] || TIPO_ICON.carico;
                const Icon = ti.icon;
                return (
                  <tr key={m.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`movimento-${m.id}`}>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {new Date(m.timestamp).toLocaleString("it-IT")}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: ti.bg, color: ti.color }}>
                        <Icon className="w-3.5 h-3.5" /> {ti.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{m.prodotto_nome}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{m.prodotto_sku}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{m.magazzino}</td>
                    <td className="px-4 py-3 font-bold">
                      {m.tipo === "carico" ? "+" : m.tipo === "scarico" ? "-" : "↔"} {m.quantita}
                    </td>
                    <td className="px-4 py-3 text-xs">{m.causale}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{m.user_name || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GiacenzeView({ prodotti, categorie, loading, searchProd, setSearchProd }) {
  // Raggruppa per categoria
  const { groups, stats } = useMemo(() => {
    const q = (searchProd || "").toLowerCase().trim();
    const filtered = !q ? prodotti : prodotti.filter((p) =>
      (p.nome || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q) ||
      (p.fornitore || "").toLowerCase().includes(q)
    );
    const byCat = {};
    for (const p of filtered) {
      const cat = p.categoria || "altro";
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(p);
    }
    // Stats globali
    let totV = 0, totF = 0, valore = 0;
    for (const p of filtered) {
      totV += Number(p.quantita_virtuale) || 0;
      totF += Number(p.quantita_fisica) || 0;
      valore += (Number(p.quantita_virtuale) + Number(p.quantita_fisica)) * Number(p.costo_unitario || 0);
    }
    return { groups: byCat, stats: { totV, totF, valore, count: filtered.length } };
  }, [prodotti, searchProd]);

  const catLabel = (slug) => {
    const c = categorie.find((x) => x.slug === slug);
    return c?.label || (slug || "Altro").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  }

  // Ordine categorie: prima quelle definite in "materiali_categorie", poi le altre
  const orderedCats = [
    ...categorie.map((c) => c.slug).filter((s) => groups[s]),
    ...Object.keys(groups).filter((s) => !categorie.find((c) => c.slug === s)),
  ];

  return (
    <div data-testid="giacenze-view">
      {/* Totali globali */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div className="card-shell p-4">
          <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Articoli attivi</div>
          <div className="text-2xl font-extrabold mt-1" style={{ color: "var(--primary)", fontFamily: "Manrope" }}>{stats.count}</div>
        </div>
        <div className="card-shell p-4">
          <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Totale virtuale</div>
          <div className="text-2xl font-extrabold mt-1" style={{ color: "var(--primary)", fontFamily: "Manrope" }}>{stats.totV.toLocaleString("it-IT")}</div>
        </div>
        <div className="card-shell p-4">
          <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Totale fisico</div>
          <div className="text-2xl font-extrabold mt-1" style={{ color: "var(--primary)", fontFamily: "Manrope" }}>{stats.totF.toLocaleString("it-IT")}</div>
        </div>
        <div className="card-shell p-4">
          <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Valore stock</div>
          <div className="text-2xl font-extrabold mt-1" style={{ color: "var(--accent)", fontFamily: "Manrope" }}>€ {stats.valore.toLocaleString("it-IT", { maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      {/* Search */}
      <div className="card-shell p-3 mb-4">
        <input
          value={searchProd}
          onChange={(e) => setSearchProd(e.target.value)}
          placeholder="Cerca per nome prodotto, SKU o fornitore…"
          className="w-full px-3 py-2 text-sm rounded-md border outline-none"
          style={{ borderColor: "var(--border)", background: "#fff" }}
          data-testid="giacenze-search"
        />
      </div>

      {orderedCats.length === 0 ? (
        <div className="card-shell p-12 text-center text-sm" style={{ color: "var(--text-muted)" }}>
          Nessun prodotto in magazzino
        </div>
      ) : (
        <div className="space-y-4">
          {orderedCats.map((slug) => {
            const rows = groups[slug] || [];
            const subV = rows.reduce((s, p) => s + (Number(p.quantita_virtuale) || 0), 0);
            const subF = rows.reduce((s, p) => s + (Number(p.quantita_fisica) || 0), 0);
            const subVal = rows.reduce((s, p) => s + (Number(p.quantita_virtuale) + Number(p.quantita_fisica)) * Number(p.costo_unitario || 0), 0);
            return (
              <div key={slug} className="card-shell overflow-hidden" data-testid={`giacenze-cat-${slug}`}>
                <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b" style={{ borderColor: "var(--border)", background: "var(--surface-muted)" }}>
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}>
                    <Boxes className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>{catLabel(slug)}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#fff", color: "var(--text-muted)" }}>{rows.length} articoli</span>
                  <div className="ml-auto flex gap-4 text-xs">
                    <div><span style={{ color: "var(--text-muted)" }}>Virt.</span> <strong>{subV}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Fis.</span> <strong>{subF}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Valore</span> <strong style={{ color: "var(--accent)" }}>€ {subVal.toLocaleString("it-IT", { maximumFractionDigits: 0 })}</strong></div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        {["Prodotto", "SKU", "Fornitore", "Costo unit.", "Virtuale", "Fisico", "Totale", "Min.", "Valore"].map((h) => (
                          <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p) => {
                        const tot = (Number(p.quantita_virtuale) || 0) + (Number(p.quantita_fisica) || 0);
                        const min = Number(p.scorta_minima ?? 5);
                        const val = tot * Number(p.costo_unitario || 0);
                        const lowStock = tot < min;
                        return (
                          <tr key={p.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`giacenza-${p.id}`}>
                            <td className="px-4 py-3 font-semibold">{p.nome}</td>
                            <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{p.sku || "—"}</td>
                            <td className="px-4 py-3 text-xs">{p.fornitore || "—"}</td>
                            <td className="px-4 py-3 text-xs">€ {Number(p.costo_unitario || 0).toFixed(2)}</td>
                            <td className="px-4 py-3">{p.quantita_virtuale}</td>
                            <td className="px-4 py-3">{p.quantita_fisica}</td>
                            <td className="px-4 py-3 font-bold">
                              <span style={{ color: lowStock ? "#B30000" : "var(--primary)" }}>{tot}</span>
                              {lowStock && <AlertTriangle className="inline w-3.5 h-3.5 ml-1" style={{ color: "#E07A5F" }} />}
                            </td>
                            <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{min}</td>
                            <td className="px-4 py-3 text-xs font-semibold">€ {val.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
